import { VdsIconButton } from '@volt/volt-extended-components-react';
import styles from './AssetCard.module.scss';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getObjectType } from '@cas/utils/translationHelpers';
import blackArrow from '@cas/assets/images/black-arrow.svg';
import blueArrow from '@cas/assets/images/blue-arrow.svg';
import grayArrow from '@cas/assets/images/gray-arrow.svg';
import {
  InvestigationAssetTrackCompleteness,
  InvestigationAssetType,
} from '@customer/store/apis/investigationsApi';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@customer/store/hooks';
import { useAssetControl } from './useAssetControl';
import BadgeDot, { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import { ObjectTypeId } from '@dmc/ui';
import { InteractiveStatusCard } from '@cas/components/data-display/interactive-status-card/InteractiveStatusCard';
import classNames from 'classnames';

const AssetIconUrl: Record<InvestigationAssetType, string> = {
  [InvestigationAssetType.Local]: blackArrow,
  [InvestigationAssetType.Remote]: blueArrow,
  [InvestigationAssetType.Nearby]: grayArrow,
};

const statusColorMap: Record<InvestigationAssetTrackCompleteness, BadgeStatus> = {
  None: BadgeStatus.Neutral,
  Partial: BadgeStatus.Information,
  Full: BadgeStatus.Neutral,
  OneSecondFailed: BadgeStatus.Danger,
  OneSecondRequested: BadgeStatus.Information,
  OneSecondPartial: BadgeStatus.Information,
  OneSecondFull: BadgeStatus.Healthy,
};

interface AssetCardProps {
  assetId: number;
  name: string;
  type: InvestigationAssetType;
  objectType?: ObjectTypeId;
  badStatus: boolean;
  noData: boolean;
  cmp: InvestigationAssetTrackCompleteness;
}

export const AssetCard = ({
  assetId,
  type,
  name,
  objectType,
  badStatus,
  noData,
  cmp,
}: AssetCardProps): ReactElement => {
  const { t } = useTranslation();
  const { investigationId } = useParams();

  const siteId = useAppSelector(state => state.auth.siteId);

  const objectName = useMemo<string>(() => getObjectType(t, objectType), [t, objectType]);
  const displayName = useMemo<string>(
    () => (name.length > 8 ? `${name.substring(0, 8)}...` : name),
    [name],
  );

  const { isVisible, isDisabled, onToggleVisibility, onShowSidePanel } = useAssetControl({
    assetId,
    type,
    siteId,
    investigationId,
    noData,
  });

  // Only change the data availability styling for the 'None' status.
  // OneSecondFailed might also have no data but this does not change the card styling.
  const displayUnavailable = noData && cmp === InvestigationAssetTrackCompleteness.None;

  return (
    <InteractiveStatusCard
      status={statusColorMap[cmp]}
      onClick={onShowSidePanel}
      isUnavailable={displayUnavailable}
    >
      <div className={styles.content}>
        <img
          className={styles.icon}
          src={AssetIconUrl[type]} // Todo: If an Object Type is specified, use the relevant avatar icon.
          alt={t(`investigationAssetType.${type}`)}
        />
        <div className={styles.details}>
          <div
            className={classNames(styles.details__name, {
              [styles['details__name--unavailable']]: displayUnavailable,
            })}
          >
            {displayName}
            {badStatus && <BadgeDot status={BadgeStatus.Danger} size={8} />}
          </div>
          <div className={styles.details__object}>{objectName}</div>
        </div>
        {/* Prevent the child button click from bubbling to the parent card. */}
        <div onClick={event => event.stopPropagation()}>
          <VdsIconButton
            icon={isVisible ? 'show' : 'hide'}
            size="large"
            variant="tertiary"
            onClick={onToggleVisibility}
            disabled={isDisabled}
          />
        </div>
      </div>
    </InteractiveStatusCard>
  );
};

export default AssetCard;
